import React from 'react';
import { Box, Pagination, Stack, Typography } from '@mui/material';
import { ModalContainer } from '@tassomutuo-backoffice/ui';

import useDateFormat from '../../../hooks/useDateFormat';
import { ManagedLeadOwner } from '../../../services/api/managed-leads/managedLeads';
import { getHsEmailInLocalStorage } from '../../../services/localStorage/localStorage';
import { selectUser, useUserStore } from '../../../store/user';
import { ManagedLeadNotesState } from '../hooks/useManagedLeadNotes';
import {
  EMPTY_MANAGED_LEAD_VALUE,
  getManagedLeadFieldValue,
} from '../managedLeadFormatting';
import {
  sanitizeManagedLeadNote,
  visibleManagedLeadNotes,
} from '../managedLeadSecurity';
import TassoQuintoErrorAlert from './TassoQuintoErrorAlert';

interface ManagedLeadNotesModalProps {
  open: boolean;
  handleClose: () => void;
  notesState: ManagedLeadNotesState;
}

const ManagedLeadNotesModal = ({
  open,
  handleClose,
  notesState,
}: ManagedLeadNotesModalProps) => {
  const { formatDate } = useDateFormat();
  const user = useUserStore(selectUser);
  const { page, setPage, query, notes: allNotes } = notesState;
  const impersonatedEmail = getHsEmailInLocalStorage();
  const notes = visibleManagedLeadNotes(allNotes, {
    isAdmin: user?.role === 'admin',
    viewerEmails: [user?.email, impersonatedEmail].filter(
      (email): email is string => !!email
    ),
  });

  return (
    <ModalContainer
      open={open}
      handleClose={handleClose}
      title="Lista note"
      containerSx={{ maxWidth: '500px' }}
      mtHeader={0}
    >
      <Box width="100%" pb={3}>
        {query.isPending && (
          <Typography role="status" color="text.secondary" mt={1}>
            Caricamento note…
          </Typography>
        )}

        {query.isError && (
          <Box mt={2}>
            <TassoQuintoErrorAlert
              error={query.error}
              retry={() => query.refetch()}
              retryableMessage="Non è stato possibile caricare le note."
              unauthorizedMessage="Non sei autorizzato a vedere le note di questa lead."
              notFoundMessage="Le note di questa lead non sono più disponibili."
            />
          </Box>
        )}

        {query.data && notes.length === 0 && (
          <Typography color="text.secondary" mt={1}>
            Nessuna nota disponibile.
          </Typography>
        )}

        {notes.length > 0 && (
          <Stack spacing={2} mt={2}>
            {notes.map((note) => {
              const noteDate = note.createdAt
                ? formatDate(note.createdAt, 'dd/MM/yyyy HH:mm')
                : EMPTY_MANAGED_LEAD_VALUE;
              const author = getManagedLeadFieldValue(
                note.author
              ) as ManagedLeadOwner | null;
              const sanitizedText = sanitizeManagedLeadNote(note.text);

              return (
                <Box
                  component="article"
                  key={note.id}
                  aria-label={`Nota del ${noteDate}`}
                  sx={{ border: '1px solid grey', p: 2 }}
                >
                  <Typography fontWeight="bold">
                    Nota del {noteDate}
                  </Typography>
                  {author && (
                    <Typography sx={{ fontSize: '12px' }}>
                      scritta da {author.name ? author.name : '-'} -{' '}
                      {author.email ? author.email : '-'}
                    </Typography>
                  )}
                  {sanitizedText ? (
                    <Box
                      mt={2}
                      sx={{
                        overflowWrap: 'anywhere',
                        '& > :first-of-type': { mt: 0 },
                        '& > :last-child': { mb: 0 },
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizedText }}
                    />
                  ) : (
                    <Typography mt={2}>{EMPTY_MANAGED_LEAD_VALUE}</Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {query.data && query.data.totalPages > 1 && (
          <Box display="flex" justifyContent="center" pt={3}>
            <Pagination
              page={page}
              count={query.data.totalPages}
              color="primary"
              onChange={(_, nextPage) => setPage(nextPage)}
            />
          </Box>
        )}
      </Box>
    </ModalContainer>
  );
};

export default ManagedLeadNotesModal;
